import { useCallback, useMemo } from 'react';
import { t } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';
import Search from '@decisiv/iconix/lib/components/Search';

import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import * as ManagementLayout from 'components/ManagementLayout';

import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import EmptyResults from 'components/EmptyResults';

import SearchableFilter from 'components/SearchableFilter';
import { SearchProvider } from 'contexts/SearchContext';
import { useManagedOemSegmentsQuery } from './hooks/useManagedSegmentsQuery';
import useOemSegmentsFilters from './hooks/useOemSegmentsFilters';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import { NoWrapHeaderTable } from '../components/styles';
import { generateColumns } from './columns';
import { ClearAllButton, SearchInputWrapper } from '../styles';
import useTableUrlState from '../hooks/useTableUrlState';

export type ManagedOemSegmentType = ExtractFromArray<
  ManagedOemSegmentsQuery['managedOemSegments']['collection']
>;

const OemSegments = () => {
  const { isSuperUser } = useCurrentUserContext();

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const {
    page,
    setPage,
    apiSort,
    defaultSort,
    onSort,
    liveSearch,
    setLiveSearch,
    debouncedSearch,
  } = useTableUrlState<OemSegmentsSortColumns>('management-oem-segments');

  const handleOnTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLiveSearch(e.target.value);
    },
    [setLiveSearch],
  );

  const { data, loading } = useManagedOemSegmentsQuery({
    variables: {
      sort: apiSort,
      page,
      filters: {
        ids: pageParams.oemSegmentIds,
        text: debouncedSearch.length > 2 ? debouncedSearch : undefined,
      },
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const tableData = data?.managedOemSegments.collection ?? [];
  const pagination = data?.managedOemSegments.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {isSuperUser && (
            <Button
              disabled
              text={t`Create OEM Segment`}
              icon={Plus}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <SearchInputWrapper>
            <SearchInput
              value={liveSearch}
              onChange={handleOnTextChange}
              hideLabel
              aria-label={t`Search Segments by Name, Segment ID, Segment Key`}
              helpMessage={t`Search Segments by Name, Segment ID, Segment Key`}
              name="searchBySegment"
              size="small"
            />
          </SearchInputWrapper>
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          {isFiltersDirty && (
            <ClearAllButton
              variant="ghost"
              onClick={handleClearFilters}
              text={t`Clear All`}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Content.Filters>
        {loading && (
          <Loading
            flex={1}
            justifyContent="center"
            size="large"
            title={t`Loading...`}
          />
        )}
        {!loading && tableData.length === 0 && (
          <EmptyResults
            title={t`No Matches for "${debouncedSearch}"`}
            subtitle={t`Check that all words are spelled correctly or search by a different value.`}
            icon={Search}
          />
        )}
        {!loading && tableData.length > 0 && (
          <SearchProvider searchString={debouncedSearch}>
            <NoWrapHeaderTable
              height="100%"
              scroll={['x', 'y']}
              data={tableData}
              columns={columns}
              onSort={onSort}
              defaultSort={defaultSort}
              footer={() => (
                <PaginationFooter
                  // @ts-expect-error - JS file
                  title={t`Oem Segments`}
                  setCurrentPage={setPage}
                  totalOnPage={tableData.length}
                  page={pagination?.page}
                  perPage={pagination?.perPage}
                  totalEntries={pagination?.totalEntries}
                  totalPages={pagination?.totalPages}
                />
              )}
            />
          </SearchProvider>
        )}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default OemSegments;
